import {
  Box,
  INVALID_BOX_MESSAGE,
  isValidBox,
  parseBox,
  round,
} from './boundingBox';

// Polygon helpers shared by the map widget and the save validators. Kept out of
// the widget so the validators do not pull in Leaflet.
//
// The stored value is a GeoJSON Polygon geometry: {"type": "Polygon",
// "coordinates": [[[lng, lat], ...]]}, outer ring first, each ring closed (its
// last position repeats the first). A field typed "object" stores the geometry
// itself; any other field stores it as a JSON string.
export type Position = [number, number];
export type Polygon = { type: 'Polygon'; coordinates: Position[][] };

export const POLYGON_WIDGET = 'polygon';

// Edge numbers typed into the widget that aren't a valid box yet. A text field
// keeps them as the four strings; an object field keeps them under this key so
// the field still holds an object. Either way the save check refuses them.
export const TYPED_EDGES_KEY = 'typed_edges';
export const typedEdgesOf = (value: unknown): unknown[] | null => {
  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      const raw = JSON.parse(value);
      return Array.isArray(raw) ? raw : null;
    } catch {
      return null;
    }
  }
  const raw = (value as any)?.[TYPED_EDGES_KEY];
  return Array.isArray(raw) ? raw : null;
};
export const INVALID_POLYGON_MESSAGE =
  "Enter a valid area: a polygon with at least three corners, edges that don't cross, latitudes between -90 and 90 and longitudes between -180 and 180.";
export const IMPORT_POLYGON_MESSAGE =
  "This isn't a GeoJSON polygon without holes. Use a Polygon, or a Feature or FeatureCollection that holds one polygon.";

const parseJson = (value: unknown): any => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

// The polygons in any GeoJSON object, as lists of rings.
const polygonsIn = (g: any): unknown[][] => {
  if (!g || typeof g !== 'object') return [];
  switch (g.type) {
    case 'Polygon':
      return [g.coordinates];
    case 'MultiPolygon':
      return Array.isArray(g.coordinates) ? g.coordinates : [];
    case 'Feature':
      return polygonsIn(g.geometry);
    case 'FeatureCollection':
      return Array.isArray(g.features) ? g.features.flatMap(polygonsIn) : [];
    case 'GeometryCollection':
      return Array.isArray(g.geometries)
        ? g.geometries.flatMap(polygonsIn)
        : [];
    default:
      return [];
  }
};

const toRing = (ring: unknown): Position[] | null => {
  if (!Array.isArray(ring)) return null;
  const points: Position[] = [];
  for (const p of ring) {
    if (!Array.isArray(p) || p.length < 2) return null;
    // GeoJSON positions are numbers. Number() would turn null, "" or false
    // into 0 and quietly move a corner to the equator or the prime meridian.
    const [lng, lat] = p;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    points.push([round(lng), round(lat)]);
  }
  return closeRing(withoutRepeats(points));
};

const samePoint = (a: Position, b: Position) => a[0] === b[0] && a[1] === b[1];

// Drops a corner identical to the one before it (after rounding). Exports from
// GIS tools often repeat a vertex or the closing point; a zero-length edge
// would otherwise read as edges crossing.
const withoutRepeats = (points: Position[]): Position[] =>
  points.filter((pt, i) => i === 0 || !samePoint(pt, points[i - 1]));

const closeRing = (points: Position[]): Position[] =>
  points.length && !samePoint(points[0], points[points.length - 1])
    ? [...points, points[0]]
    : points;

// The one polygon held by a stored value or an imported GeoJSON object, or null
// when there is none, more than one, or a position is not a pair of numbers. A
// parsed polygon can still be out of range; check it with isValidPolygon.
export const parsePolygon = (value: unknown): Polygon | null => {
  const polygons = polygonsIn(parseJson(value));
  if (polygons.length !== 1 || !Array.isArray(polygons[0])) return null;
  const rings = polygons[0].map(toRing);
  if (!rings.length || rings.some((r) => r === null)) return null;
  return { type: 'Polygon', coordinates: rings as Position[][] };
};

// Twice the ring's signed area (shoelace); zero for corners in a straight line.
const doubleArea = (ring: Position[]) =>
  ring
    .slice(0, -1)
    .reduce(
      (sum, [x1, y1], i) => sum + x1 * ring[i + 1][1] - ring[i + 1][0] * y1,
      0,
    );

const orientation = (a: Position, b: Position, c: Position) =>
  Math.sign((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]));

const onSegment = (a: Position, b: Position, p: Position) =>
  Math.min(a[0], b[0]) <= p[0] &&
  p[0] <= Math.max(a[0], b[0]) &&
  Math.min(a[1], b[1]) <= p[1] &&
  p[1] <= Math.max(a[1], b[1]);

// Whether segments ab and cd share any point, touching included.
const segmentsMeet = (a: Position, b: Position, c: Position, d: Position) => {
  const o1 = orientation(a, b, c);
  const o2 = orientation(a, b, d);
  const o3 = orientation(c, d, a);
  const o4 = orientation(c, d, b);
  if (o1 !== o2 && o3 !== o4) return true;
  return (
    (o1 === 0 && onSegment(a, b, c)) ||
    (o2 === 0 && onSegment(a, b, d)) ||
    (o3 === 0 && onSegment(c, d, a)) ||
    (o4 === 0 && onSegment(c, d, b))
  );
};

// Whether two edges of a closed ring that aren't neighbours meet. Drawing
// already refuses this; imported GeoJSON is checked here. Edges are swept in
// order of their left end, so only edges whose x-ranges overlap are compared:
// near-linear for real outlines, which keeps a 5,000-corner import fast.
const crossesItself = (ring: Position[]) => {
  const count = ring.length - 1;
  const edges = Array.from({ length: count }, (_, i) => {
    const [a, b] = [ring[i], ring[i + 1]];
    return {
      i,
      a,
      b,
      minX: Math.min(a[0], b[0]),
      maxX: Math.max(a[0], b[0]),
      minY: Math.min(a[1], b[1]),
      maxY: Math.max(a[1], b[1]),
    };
  }).sort((e, f) => e.minX - f.minX);
  const neighbours = (i: number, j: number) =>
    Math.abs(i - j) === 1 || Math.abs(i - j) === count - 1;
  for (let k = 0; k < edges.length; k++) {
    const e = edges[k];
    for (let m = k + 1; m < edges.length && edges[m].minX <= e.maxX; m++) {
      const f = edges[m];
      if (f.minY > e.maxY || f.maxY < e.minY || neighbours(e.i, f.i)) continue;
      if (segmentsMeet(e.a, e.b, f.a, f.b)) return true;
    }
  }
  return false;
};

// One outer edge only. Drawing never makes holes, and a hole would also have
// to sit inside the outer edge and clear of other holes to be valid.
export const isValidPolygon = (p: Polygon): boolean =>
  p.coordinates.length === 1 &&
  p.coordinates.every(
    (ring) =>
      // Three distinct corners plus the closing repeat of the first.
      new Set(ring.map((pt) => pt.join(','))).size >= 3 &&
      ring.every(
        ([lng, lat]) => lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90,
      ) &&
      doubleArea(ring) !== 0 &&
      !crossesItself(ring),
  );

export const serializePolygon = (p: Polygon, asObject: boolean) =>
  asObject ? p : JSON.stringify(p);

// Corners from the map, clamped to the valid range: the map can show more than
// one world (zoomed out, or panned across the date line).
export const polygonFromLatLngs = (
  rings: { lat: number; lng: number }[][],
): Polygon => ({
  type: 'Polygon',
  coordinates: rings.map((ring) =>
    closeRing(
      withoutRepeats(
        ring.map(({ lat, lng }) => [
          round(Math.min(180, Math.max(-180, lng))),
          round(Math.min(90, Math.max(-90, lat))),
        ]),
      ),
    ),
  ),
});

// A box as a closed rectangle ring, and back. The map widget works on polygons
// in both modes; a box-mode field still stores the box's four numbers.
export const boxToPolygon = (b: Box): Polygon => ({
  type: 'Polygon',
  coordinates: [
    [
      [round(b.west), round(b.south)],
      [round(b.east), round(b.south)],
      [round(b.east), round(b.north)],
      [round(b.west), round(b.north)],
      [round(b.west), round(b.south)],
    ],
  ],
});

export const polygonToBox = (p: Polygon): Box => {
  const ring = p.coordinates[0];
  const lngs = ring.map((pt) => pt[0]);
  const lats = ring.map((pt) => pt[1]);
  return {
    west: Math.min(...lngs),
    south: Math.min(...lats),
    east: Math.max(...lngs),
    north: Math.max(...lats),
  };
};

// An axis-aligned four-corner polygon, so the map can edit it as a rectangle.
export const isRectangle = (p: Polygon): boolean => {
  if (p.coordinates.length !== 1) return false;
  const ring = p.coordinates[0];
  if (ring.length !== 5) return false;
  const lngs = new Set(ring.map((pt) => pt[0]));
  const lats = new Set(ring.map((pt) => pt[1]));
  return (
    lngs.size === 2 &&
    lats.size === 2 &&
    ring.slice(0, 4).every((pt, i) => {
      const next = ring[i + 1];
      return pt[0] === next[0] || pt[1] === next[1];
    })
  );
};

// Save-time check for every field the connector marks with ui:widget "polygon":
// a non-empty value must be a valid polygon. Returns { field: message }.
export const polygonErrors = (
  uiSchema: any,
  data: any,
  t: (key: any, options?: any) => string = (key: any) => key,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  Object.entries(uiSchema || {}).forEach(([field, options]: [string, any]) => {
    if (options?.['ui:widget'] !== POLYGON_WIDGET) return;
    const value = data?.[field];
    if (value === null || value === undefined || value === '') return;
    const polygon = parsePolygon(value);
    if (polygon && isValidPolygon(polygon)) return;
    // A box saved while the field still asked for "bbox" stays valid: the
    // widget shows it as a rectangle and the next edit saves it as GeoJSON.
    const box = parseBox(value);
    if (box && isValidBox(box)) return;
    // Edge numbers typed into the widget and not a valid box yet are stored
    // as the four strings; give them the same message the widget shows.
    errors[field] = t(
      typedEdgesOf(value) ? INVALID_BOX_MESSAGE : INVALID_POLYGON_MESSAGE,
    );
  });
  return errors;
};
